/**
 * DataBest — Alerts store (zustand, in-memory).
 *
 * Centralised source of truth for backlog summary counters so that:
 *   - AppLayout sidebar badge and TodayPage banner stay in sync
 *   - A successful measurement save can invalidate the summary imperatively
 *     (via `refresh()`) without waiting for the 60s poll
 *   - Multiple simultaneous fetches are deduplicated (in-flight promise reuse)
 */
import { create } from "zustand";
import { backlogApi } from "@/lib/backlogApi";

const DEFAULT_SUMMARY = { due: 0, overdue: 0, critical: 0, total: 0 };

let inflight = null;

const useAlertsStore = create((set, get) => ({
    summary: DEFAULT_SUMMARY,
    loading: false,
    lastFetchedAt: null,

    refresh: async () => {
        if (inflight) return inflight;
        set({ loading: true });
        inflight = backlogApi
            .summary()
            .then((data) => {
                set({
                    summary: {
                        due: data?.due || 0,
                        overdue: data?.overdue || 0,
                        critical: data?.critical || 0,
                        total: data?.total || 0,
                    },
                    loading: false,
                    lastFetchedAt: Date.now(),
                });
                return data;
            })
            .catch(() => {
                set({ loading: false });
            })
            .finally(() => {
                inflight = null;
            });
        return inflight;
    },

    reset: () => {
        inflight = null;
        set({ summary: DEFAULT_SUMMARY, loading: false, lastFetchedAt: null });
    },
}));

export default useAlertsStore;
